import React, { useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import { HOST } from "../../common/constant";
import axiosInstance from "../../common/interceptor";
import "./AsphaltDashboard.css";
// ① アイコンイメージ: ヘッダーの文字「運搬中温度管理システム」を pita@ Dashboard ロゴに差し替える。
import dashboardLogo from "../../assets/img/PitaDashboardIcon.png";

const logout = () => {
  Cookies.remove("user");
  Cookies.remove("jwt");
  window.location.href = "/login";
};

/**
 * 合材管理ダッシュボード メイン画面（運搬中温度管理システム）。
 * サーバー(DAT_ASPHALT_TRIP 他)を読むだけの表示専用。温度取得・判定はバックエンド(ダッシュボード)が行う。
 * 30秒ごとにポーリングして最新化。
 */
interface ICountCell {
  count: number;
  loadTon: number;
}
interface IBoardRow {
  id?: number; // SKY-51: トリップID（積載量セルのクリック遷移で使用）
  cliID: number;
  terminalName?: string;
  owner?: string;
  daiMe?: number | null;
  state?: string;
  latestTemp?: number | null;
  loadTon?: number | null;
  departTime?: string | null;
  departTemp?: number | null;
  arriveEstTime?: string | null;
  arriveTime?: string | null;
  arriveTemp?: number | null;
  unloadTime?: string | null;
  unloadTemp?: number | null;
  remainMin?: number | null;
  isLiveEta?: boolean; // SKY-146①: true=ライブ到着予想使用中(赤字) / false=通常ETA(黒字)
  pitaComm?: string;   // SKY-49: 通信状態(ぴたあっと) on/stale/off
  deviceComm?: string; // SKY-49: 通信状態(温度デバイス) on/stale/off
}
interface IBoard {
  rows: IBoardRow[];
  unloaded: ICountCell;
  inTransit: ICountCell;
  arrived: ICountCell;
  total: ICountCell;
  cautionCount: number;
  warningCount: number;
  alarmCaution: number;
  alarmWarning: number;
  workStart?: string;
  deviceComm?: string; // on/stale/off
  pitaComm?: string;
  displayItems?: string;
  displayOrder?: string; // SKY-77: カラムの並び順 CSV
  dateFormat?: string;   // 日付表示設定: "time"=時刻のみ / それ以外=年月日と時刻
}

/** ボードのカラム定義（表示項目設定と共有）。 */
export const BOARD_COLUMNS: { key: string; label: string }[] = [
  { key: "daiMe", label: "台目" },
  { key: "terminalName", label: "端末名" },
  { key: "owner", label: "所有者名" },
  { key: "state", label: "状態" },
  { key: "latestTemp", label: "最新温度(℃)" },
  { key: "loadTon", label: "積載量(t)" },
  { key: "departTime", label: "出発時刻" },
  { key: "departTemp", label: "出発温度(℃)" },
  { key: "arriveEstTime", label: "到着予想時刻" },
  { key: "arriveTime", label: "到着時刻" },
  { key: "arriveTemp", label: "到着温度(℃)" },
  { key: "unloadTime", label: "荷降し時刻" },
  { key: "unloadTemp", label: "荷降し温度(℃)" },
  { key: "remainMin", label: "残り時間(分)" },
];

const EMPTY: IBoard = {
  rows: [],
  unloaded: { count: 0, loadTon: 0 },
  inTransit: { count: 0, loadTon: 0 },
  arrived: { count: 0, loadTon: 0 },
  total: { count: 0, loadTon: 0 },
  cautionCount: 0,
  warningCount: 0,
  alarmCaution: 165,
  alarmWarning: 160,
  workStart: "",
  deviceComm: "off",
  pitaComm: "on",
  displayItems: "",
  displayOrder: "",
  dateFormat: "time",
};

/**
 * SKY-77: 保存された並び順(CSV)に従って BOARD_COLUMNS を並べ替える。
 * - order が空 / 未保存 → 既定順(定義順)をそのまま返す。
 * - order に無いキー（新カラム追加時など）は末尾に既定順で追加する。
 * - order にある未知のキー（カラム削除時など）は無視する。
 * これにより BOARD_COLUMNS を将来変更しても保存済みの並び順が壊れない。
 */
export const orderColumns = (order?: string): { key: string; label: string }[] => {
  const keys = (order || "").split(",").filter(Boolean);
  if (keys.length === 0) return [...BOARD_COLUMNS];
  const byKey = new Map(BOARD_COLUMNS.map((c) => [c.key, c]));
  const ordered = keys.map((k) => byKey.get(k)).filter((c): c is { key: string; label: string } => !!c);
  const seen = new Set(ordered.map((c) => c.key));
  return [...ordered, ...BOARD_COLUMNS.filter((c) => !seen.has(c.key))];
};

// カラム key → セル描画。df=日付表示設定(時刻フォーマット)。
const CELL: Record<string, (r: IBoardRow, df?: string) => React.ReactNode> = {
  // SKY-53: 台目 のセル(値)クリックで出荷キャンセルへ遷移する。
  daiMe: (r) => (
    <span
      className="asp-cell-link"
      title="クリックで出荷キャンセル"
      onClick={() => { window.location.href = "/asphalt-cancel?id=" + (r.id ?? ""); }}
    >
      {r.daiMe ?? ""}
    </span>
  ),
  terminalName: (r) => r.terminalName,
  owner: (r) => r.owner,
  state: (r) => r.state,
  latestTemp: (r) => t1(r.latestTemp),
  // SKY-51: 積載量(t) のセル(値)クリックで積載量編集へ遷移する。
  loadTon: (r) => (
    <span
      className="asp-cell-link"
      title="クリックで積載量編集"
      onClick={() => { window.location.href = "/asphalt-load?id=" + (r.id ?? ""); }}
    >
      {t2(r.loadTon)}
    </span>
  ),
  departTime: (r, df) => hm(r.departTime, df),
  departTemp: (r) => t1(r.departTemp),
  // 設計 ㉜「到着予想時刻セル」: デフォルトは黒、ライブ到着予想時刻使用時のみ赤。
  // SKY-146①: 従来は「値が入っていれば赤」だったため全行が赤になっていた。サーバの isLiveEta で切り替える。
  arriveEstTime: (r, df) => <span className={r.isLiveEta ? "asp-eta-live" : ""}>{hm(r.arriveEstTime, df)}</span>,
  arriveTime: (r, df) => hm(r.arriveTime, df),
  arriveTemp: (r) => t1(r.arriveTemp),
  unloadTime: (r, df) => hm(r.unloadTime, df),
  unloadTemp: (r) => t1(r.unloadTemp),
  // 設計 ㊲「残り時間セル」: デフォルトは黒、ライブ到着予想時刻使用時のみ赤。
  // SKY-146①: 従来は色指定が無く常に黒だった。到着予想時刻と同じ基準(isLiveEta)で塗る。
  remainMin: (r) => <span className={r.isLiveEta ? "asp-eta-live" : ""}>{r.remainMin ?? ""}</span>,
};

const commDot = (s?: string): string => (s === "on" ? "asp-dot-on" : s === "stale" ? "asp-dot-stale" : "asp-dot-off");

const t1 = (v?: number | null): string => (v === null || v === undefined ? "" : v.toFixed(1));
const t2 = (v?: number | null): string => (v === null || v === undefined ? "" : v.toFixed(2));
// 日付表示設定(df): "time"=時刻のみ(HH:mm) / それ以外(年月日と時刻)=yyyy/MM/dd HH:mm。
// 設計「出発時刻セル フォーマット HH:mm」+ 日付表示設定「年月日と時刻」(年を含む)。
const hm = (s?: string | null, df?: string): string => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  const p = (n: number) => `${n}`.padStart(2, "0");
  const time = `${p(d.getHours())}:${p(d.getMinutes())}`;
  return df === "time" || df === undefined
    ? time
    : `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${time}`;
};
const fmtWorkStart = (ws?: string): string => {
  if (!ws) return "—";
  const p = ws.split("|");
  if (p[0] === "daily") return `毎日 ${p[1] || ""} 開始`;
  if (p[0] === "range") return `${p[1] || ""} ～ ${p[2] || ""}`;
  return ws;
};
const nowStr = (): string => {
  const d = new Date();
  const p = (n: number) => `${n}`.padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const AsphaltDashboard: React.FC = () => {
  const [board, setBoard] = useState<IBoard>(EMPTY);
  const [now, setNow] = useState<string>(nowStr());
  // SKY-54: ヘッダーのハンバーガーメニュー開閉 + バージョン情報ダイアログ。
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [showVersion, setShowVersion] = useState<boolean>(false);

  // ヘルプ→マニュアル: サーバーのマニュアルPDFをダウンロードする（設計「マニュアルをダウンロードする」）。
  const downloadManual = async () => {
    try {
      const res = await fetch(HOST() + "/Form/V1/File/Manual", {
        method: "GET",
        headers: { authorization: Cookies.get("jwt") || "" },
      });
      if (!res.ok) { window.alert("マニュアルが見つかりません。"); return; }
      const blob = await res.blob();
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = "ぴたあっと合材管理ダッシュボードマニュアル.pdf";
      link.href = href;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(href);
    } catch {
      window.alert("マニュアルのダウンロードに失敗しました。");
    }
  };

  const load = useCallback(() => {
    axiosInstance
      .get(HOST() + "/Form/V1/Dat/AsphaltDashboard/Board")
      .then((res) => {
        const d = res.data?.data as IBoard | undefined;
        if (d) setBoard({ ...EMPTY, ...d });
      })
      .catch(() => {
        /* 表示専用: 失敗時は前回値維持 */
      });
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      setNow(nowStr());
      load();
    }, 30000);
    return () => clearInterval(t);
  }, [load]);

  const rowClass = (r: IBoardRow): string => {
    if (r.state === "キャンセル") return "asp-row-cancel";
    // SKY-102: 温度警報は現着(到着)でも消さず、荷降し判定まで継続する（お客様期待値）。
    //   従来は「運搬中」のみ着色していたため、現着時点で警報が消えていた。到着も対象に含める（荷降し/キャンセルは対象外）。
    if ((r.state === "運搬中" || r.state === "到着") && r.latestTemp !== null && r.latestTemp !== undefined) {
      if (r.latestTemp <= board.alarmWarning) return "asp-row-warning";
      if (r.latestTemp <= board.alarmCaution) return "asp-row-caution";
    }
    return "";
  };

  // 設計 メイン画面: ログインユーザーの「ユーザー名」を表示する（表示名→無ければログインID）。
  const loginUserName = (() => {
    try { const u = JSON.parse(Cookies.get("user") || "{}"); return u.displayName || u.loginID || ""; }
    catch { return ""; }
  })();

  const hiddenCols = new Set((board.displayItems || "").split(",").filter(Boolean));
  // SKY-77: 表示項目設定で並べ替えた順を反映してから、非表示カラムを除く。
  const visibleCols = orderColumns(board.displayOrder).filter((c) => !hiddenCols.has(c.key));

  return (
    <div className="asp-dashboard">
      {showVersion && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }} onClick={() => setShowVersion(false)}>
          <div style={{ background: "#fff", borderRadius: 6, minWidth: 300, boxShadow: "0 4px 20px rgba(0,0,0,0.35)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ background: "#cfe2f3", color: "#1f3b57", padding: "10px 16px", borderRadius: "6px 6px 0 0", fontWeight: "bold" }}>バージョン情報</div>
            <div style={{ padding: "28px 16px", textAlign: "center", fontSize: 18 }}>1.0.0</div>
            <div style={{ padding: "0 16px 18px", textAlign: "center" }}>
              <button type="button" className="btn btn-primary btn-sm" style={{ minWidth: 80 }} onClick={() => setShowVersion(false)}>OK</button>
            </div>
          </div>
        </div>
      )}
      {/* SKY-54: ヘッダーをハンバーガーメニューに変更（見本準拠）。個別ボタンはメニュー内に集約。 */}
      <div className="asp-devhead">
        <span className="asp-hamburger" onClick={() => setShowMenu((v) => !v)} title="メニュー">☰</span>
        <div className="asp-title" style={{ flex: 1 }}>
          <img src={dashboardLogo} alt="pita@ Dashboard" height={28} />
        </div>
        <span style={{ marginRight: 14, fontWeight: 500, color: "#1f3b57", verticalAlign: "middle" }} title="ログインユーザー">👤 {loginUserName}</span>
        <button type="button" className="btn btn-danger btn-sm mar-5" onClick={logout}>ログアウト</button>

        {showMenu && (
          <div className="asp-menu" onMouseLeave={() => setShowMenu(false)}>
            <div className="asp-menu-item asp-has-sub">
              <span>ヘルプ</span><span className="asp-menu-caret">›</span>
              <div className="asp-submenu">
                <div className="asp-menu-item" onClick={() => { setShowMenu(false); downloadManual(); }}>マニュアル</div>
                <div className="asp-menu-item" onClick={() => { setShowMenu(false); setShowVersion(true); }}>バージョン</div>
              </div>
            </div>
            <div className="asp-menu-item asp-has-sub">
              <span>設定</span><span className="asp-menu-caret">›</span>
              <div className="asp-submenu">
                <div className="asp-menu-item" onClick={() => (window.location.href = "/asphalt-display")}>表示項目設定</div>
                <div className="asp-menu-item" onClick={() => (window.location.href = "/asphalt-devices")}>デバイス管理</div>
                <div className="asp-menu-item" onClick={() => (window.location.href = "/asphalt-settings?type=work")}>作業開始日設定</div>
                <div className="asp-menu-item" onClick={() => (window.location.href = "/asphalt-settings?type=date")}>日付表示設定</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="asp-header">
        <span className="asp-worklabel">作業開始日時</span>
        <span className="asp-workval">{fmtWorkStart(board.workStart)}</span>
        <span className="asp-nowlabel">現在時刻</span>
        <span className="asp-nowval">{now}</span>
      </div>

      <div className="asp-panels">
        {/* 台数・積載量 集計 */}
        <table className="asp-count">
          <thead>
            <tr>
              <th></th>
              <th>荷降し</th>
              <th>運搬中</th>
              <th>到着</th>
              <th>合計</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>台数</th>
              <td>{board.unloaded.count}</td>
              <td>{board.inTransit.count}</td>
              <td>{board.arrived.count}</td>
              <td>{board.total.count}</td>
            </tr>
            <tr>
              <th>積載量(t)</th>
              <td>{t2(board.unloaded.loadTon)}</td>
              <td>{t2(board.inTransit.loadTon)}</td>
              <td>{t2(board.arrived.loadTon)}</td>
              <td>{t2(board.total.loadTon)}</td>
            </tr>
          </tbody>
        </table>

        {/* 運搬中温度警報台数 */}
        <table className="asp-alarm">
          <thead>
            <tr>
              {/* SKY-48: 列幅をそろえる（本体4列に合わせて見出しを全幅に） */}
              <th colSpan={4}>運搬中温度警報台数</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th className="asp-caution-cell">下限注意</th>
              <td>{board.cautionCount}</td>
              <th className="asp-warning-cell">下限警報</th>
              <td>{board.warningCount}</td>
            </tr>
          </tbody>
        </table>

        {/* SKY-49: 通信状態は端末ごとに（下の表の「通信状態」列で）表示する。ここは凡例のみ残す。 */}
        <div className="asp-comm">
          <div className="asp-commlegend">
            {/* SKY-73: 赤色の凡例ラベルは「通信断：」ではなく「未接続：」（メイン画面4 テスト項目⑱）。 */}
            通信中: <span className="asp-dot asp-dot-on" /> 未接続: <span className="asp-dot asp-dot-off" /> データ受信失敗: <span className="asp-dot asp-dot-stale" />
          </div>
        </div>
      </div>

      <div className="asp-board-wrap">
        <table className="asp-board">
          <thead>
            {/* SKY-49: 通信状態を端末ごとに表示（ぴたあっと / 温度デバイス の2列を右端に追加）。 */}
            <tr>
              {visibleCols.map((c) => (
                <th key={c.key} rowSpan={2}>{c.label}</th>
              ))}
              <th colSpan={2}>通信状態</th>
            </tr>
            <tr>
              <th>ぴたあっと</th>
              <th>温度デバイス</th>
            </tr>
          </thead>
          <tbody>
            {board.rows.length === 0 ? (
              <tr>
                <td colSpan={visibleCols.length + 2} className="asp-empty">データがありません</td>
              </tr>
            ) : (
              board.rows.map((r, i) => (
                <tr key={`${r.cliID}-${r.daiMe}-${i}`} className={rowClass(r)}>
                  {visibleCols.map((c) => (
                    <td key={c.key}>{CELL[c.key](r, board.dateFormat)}</td>
                  ))}
                  {/* SKY-75: 通信状態の2列は警報/キャンセルの背景色を塗らない（メイン画面4 テスト項目㉓）。 */}
                  {/* SKY-103: 現在運行中の端末の現在行のみ通信状態を表示。null(サーバ側で非対象)は空欄にする。 */}
                  <td className="asp-comm-cell">{r.pitaComm != null ? <span className={"asp-dot " + commDot(r.pitaComm)} /> : null}</td>
                  <td className="asp-comm-cell">{r.deviceComm != null ? <span className={"asp-dot " + commDot(r.deviceComm)} /> : null}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AsphaltDashboard;
